import React, { useState } from 'react';
import { Alert, Badge, Button, Form, Spinner, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  useGetBackstageDeliveryPlanQuery,
  useCreateBackstageDeliveryPlanMutation,
} from '../../redux/api/backstageDeliveryApi';
import { useApiErrorToast } from '../../hooks/useApiError';

// The backend emits naive local festival timestamps ("2026-06-26T18:15:00").
// Slice them so the browser timezone never shifts the display.
const fmtDt = (iso) =>
  iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}. ${iso.slice(11, 16)}` : '—';

const pct = (v) =>
  v === null || v === undefined ? '—' : `${(v * 100).toFixed(0)} %`;

const DeliveryCell = ({ deliveries }) => (
  <ul className='list-unstyled mb-0 small'>
    {deliveries.map((d) => (
      <li key={d.seq} className='mb-1'>
        <span className={d.is_full_box ? '' : 'fw-bold text-primary'}>
          {d.label}
        </span>{' '}
        <span className='text-muted'>
          benötigt {fmtDt(d.needed_at)} → möglich {fmtDt(d.possible_at)}
        </span>
        {d.flags?.during_show && (
          <Badge bg='warning' text='dark' className='ms-1'>
            während Show
          </Badge>
        )}
        {d.flags?.after_empty && (
          <Badge bg='danger' className='ms-1'>
            zu spät
          </Badge>
        )}
      </li>
    ))}
  </ul>
);

const ResultView = ({ plan }) => {
  if (!plan || !plan.result) {
    return (
      <p className='text-muted'>
        Noch kein Lieferplan berechnet. Bitte beide CSV-Dateien hochladen.
      </p>
    );
  }
  const r = plan.result;
  const totalPax = r.backstages.reduce((s, b) => s + b.total_pax, 0);

  return (
    <div className='mt-4'>
      <h5>
        {r.event_name || 'Lieferplan'}{' '}
        <small className='text-muted'>
          ({r.dates?.[0]} – {r.dates?.[r.dates.length - 1]})
        </small>
      </h5>
      <p className='text-muted small mb-3'>
        Box = {r.box_size} PAX · Lieferfenster {r.delivery_window?.[0]}–
        {r.delivery_window?.[1]} Uhr · {r.backstages.length} Backstages ·{' '}
        {totalPax} PAX gesamt · berechnet {fmtDt(r.generated_at)}
      </p>

      {r.warnings?.length > 0 && (
        <Alert variant='warning' className='py-2'>
          <strong>Hinweise:</strong>
          <ul className='mb-0'>
            {r.warnings.map((w) =>
              w.type === 'deviation' ? (
                <li key={w.backstage}>
                  <strong>{w.backstage}</strong>: Show-Summe weicht {pct(w.deviation_pct)}{' '}
                  vom Getränke-Sollwert ab (Sollwert maßgeblich).
                </li>
              ) : (
                <li key={w.backstage}>
                  <strong>{w.backstage}</strong>: keine Shows gefunden – Timing nicht
                  bestimmbar.
                </li>
              )
            )}
          </ul>
        </Alert>
      )}

      <Table striped bordered hover responsive size='sm' className='align-middle'>
        <thead>
          <tr>
            <th>Backstage</th>
            <th className='text-end'>PAX gesamt</th>
            <th className='text-end'>Show-Summe</th>
            <th className='text-end'>Δ</th>
            <th>Lieferungen</th>
          </tr>
        </thead>
        <tbody>
          {r.backstages.map((b) => (
            <tr key={b.name}>
              <td>{b.name}</td>
              <td className='text-end'>{b.total_pax}</td>
              <td className='text-end'>{b.has_shows ? b.shows_sum : '—'}</td>
              <td
                className={`text-end ${
                  b.deviation_flag ? 'text-danger fw-bold' : 'text-muted'
                }`}
              >
                {b.has_shows ? pct(b.deviation_pct) : '—'}
              </td>
              <td>
                <DeliveryCell deliveries={b.deliveries} />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

const BackstageDeliveryPlanner = () => {
  const [showsFile, setShowsFile] = useState(null);
  const [drinksFile, setDrinksFile] = useState(null);
  const apiErrorToast = useApiErrorToast();

  const { data: plan, isLoading } = useGetBackstageDeliveryPlanQuery();
  const [createPlan, { isLoading: isComputing }] =
    useCreateBackstageDeliveryPlanMutation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!showsFile || !drinksFile) {
      toast.error('Bitte beide Dateien auswählen.');
      return;
    }
    const formData = new FormData();
    formData.append('shows_file', showsFile);
    formData.append('drinks_file', drinksFile);
    try {
      await createPlan(formData).unwrap();
      toast.success('Lieferplan berechnet.');
    } catch (err) {
      apiErrorToast(err, 'Berechnung fehlgeschlagen.');
    }
  };

  return (
    <div className='mt-3'>
      <h4>Backstage-Getränkelieferung</h4>
      <p className='text-muted'>
        Zwei CSV-Exporte hochladen: die Getränke-Kopfzahlen (Sollwert) und den
        Show-Plan (Timing). Berechnet je Backstage die benötigten {''}
        Boxen und wann die nächste Lieferung nötig bzw. möglich ist.
      </p>

      <Form onSubmit={handleSubmit} className='row g-3 align-items-end'>
        <Form.Group className='col-md-5'>
          <Form.Label>Backstage-Drinks CSV (Sollwert)</Form.Label>
          <Form.Control
            type='file'
            accept='.csv'
            onChange={(e) => setDrinksFile(e.target.files?.[0] || null)}
          />
        </Form.Group>
        <Form.Group className='col-md-5'>
          <Form.Label>Shows CSV (Timing)</Form.Label>
          <Form.Control
            type='file'
            accept='.csv'
            onChange={(e) => setShowsFile(e.target.files?.[0] || null)}
          />
        </Form.Group>
        <div className='col-md-2'>
          <Button type='submit' disabled={isComputing} className='w-100'>
            {isComputing ? (
              <>
                <Spinner animation='border' size='sm' /> Berechne…
              </>
            ) : (
              'Berechnen'
            )}
          </Button>
        </div>
      </Form>

      {isLoading ? (
        <p className='mt-4 text-muted'>Lade letzten Lieferplan…</p>
      ) : (
        <ResultView plan={plan} />
      )}
    </div>
  );
};

export default BackstageDeliveryPlanner;
